<template>
  <div>
    <v-card
      v-if="isDropzoneImage || !hideInMaxFile"
      flat
      outlined
      :class="{ 'grey lighten-2': dragging }"
      style="cursor: pointer;"
      @dragover.stop.prevent="dragging=true"
      @dragenter.prevent="dragging=true"
      @dragleave.prevent="dragging=false"
      @drop.stop.prevent="onDrop"
    >
      <v-card-text class="pa-1">
        <input
          ref="input_file"
          :key="refreshKey"
          class="d-none"
          type="file"
          :accept="acceptFileFormat"
          :multiple="maxSize > 1"
          @change="fileSelected"
        >
        <v-row
          class="d-flex flex-column"
          dense
          align="center"
          justify="center"
          @click="showFileDialog"
        >
          <p v-if="simple" class="my-0">
            ファイルアップロード
          </p>
          <v-icon
            :class="[dragging ? 'mt-1, mb-2' : 'mt-2']" size="30"
          >
            mdi-cloud-upload
          </v-icon>
          <p v-if="!simple" style="text-align: center; margin: 0; padding: 8px 0;">
            ファイルをドラッグするか、ここをクリックしてください<br>
            【
            <template v-if="maxFileSize > 0">
              上限ファイルサイズ: {{ altFileSizeFormat }}
            </template>
            最大ファイル数: {{ maxSize }}枚 ファイル形式: {{ altFileFormat }}】
          </p>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row
      v-if="!hideThumbnail && previewableImages.length > 0"
    >
      <template
        v-for="(image, index) in previewImages"
      >
        <v-col
          v-if="!image._destroy"
          :key="index"
          cols="12" :md="mdColSize"
        >
          <v-card
            flat
            class="mt-2"
            outlined
          >
            <v-card-text>
              <v-img
                v-if="isImageContent(image.content_type)"
                :src="image.url | load_image_url"
                max-height="100"
                contain
                aspect-ratio="1"
                class="gray lighten-4 align-center"
              />
              <template
                v-else
              >
                <div class="text-center">
                  <v-icon
                    size="100"
                  >
                    {{ documentIcon(image.content_type) }}
                  </v-icon>
                </div>
              </template>
              <p
                v-if="!isEmpty(image.name)"
                style="text-align: center;"
                :title="image.name"
                class="text-ellipsis"
              >
                {{ image.name }}
              </p>
            </v-card-text>
            <v-divider />
            <v-card-actions>
              {{ convertSize(image.size) }}
              <v-spacer />
              <v-btn
                small
                icon
                color="error darken-2"
                @click="removeFile(index)"
              >
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </template>
    </v-row>
  </div>
</template>

<script>

/**
 * example
 *  <dropzone-file
 *   :files.sync="files"
 *   @fileChanged="fileChanged"
 *   />
 *  key: コンポートンと再描画用のカウンター（登録後にカウントアップなどを行えば、コンポーネントを再描画する
 *  files.sync: 登録、削除するファイルの配列
 *    既存ファイル表示: {id: 1, url: 画像URL, content_type: コンテンツタイプ, name: ファイル名, size: ファイルサイズ(byte)}
 *    既存ファイル削除: {id: 1, url: 画像URL, content_type: コンテンツタイプ, name: ファイル名, size: ファイルサイズ(byte), _destroy: true}
 *    新規ファイル: {url: 画像URL, file: ファイルオブジェクト, content_type: コンテンツタイプ, name: ファイル名, size: ファイルサイズ(byte), _destroy: false}
 *  fileChanged: ファイルを追加、削除したさいに発火するイベント
 *  許可するファイル種別「accepts」 でサムネイルを表示するか判定を行います。
 *  画像系：サムネイルを表示
 *  PDF: file-pdf-box アイコンを表示
 *  以外: file-document-outline アイコンを表示
 */

export default {
  name: 'DropzoneImage',
  props: {
    // 画像の最大数
    maxSize: {
      type: Number,
      default: 1,
    },
    // 最大ファイルサイズ(byte)
    // e.g.
    // 指定がない場合は制限なし
    // 1000000 → 1MB
    maxFileSize: {
      type: Number,
      default: 0,
    },
    // 上限サイズ（表示用）
    // e.g.
    // 5MB
    altFileSizeFormat: {
      type: String,
      default: null,
    },
    // 画像の配列
    files: {
      type: Array,
      default: () => [],
    },
    // 許可するファイル種別
    accepts: {
      type: Array,
      default: () => ['image/jpg', 'image/jpeg', 'image/png'],
    },
    // 許可する拡張子（表示用）
    altFileFormat: {
      type: String,
      default: '.jpg .jpeg .png',
    },
    // 即時削除
    immediateDelete: {
      type: Boolean,
      default: true,
    },
    // カラムサイズ
    mdColSize: {
      type: Number,
      default: 4,
    },
    simple: {
      type: Boolean,
      default: false
    },
    hideThumbnail: {
      type: Boolean,
      default: false
    },
    hideInMaxFile: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      dragging: false,
      isFileChanged: false,
      maxFiles: this.maxSize === null ? 99 : this.maxSize,
      previewImages: this.files,
      refreshKey: 0,
    }
  },
  computed: {
    maxFilesAlertMessage () {
      return `アップロードできる枚数は「${this.maxFiles}枚」までです`
    },
    acceptAlertMessage () {
      return `アップロードできる種類は「${this.altFileFormat}」です`
    },
    maxFileSizeAlertMessage () {
      return `アップロードできる上限サイズは「${this.altFileSizeFormat}」までです`
    },
    acceptFileFormat () {
      return this.accepts ? this.accepts.join(',') : null
    },
    previewableImages () {
      return this.previewImages.filter(m => !m._destroy)
    },
    isDropzoneImage () {
      return this.maxSize > this.previewableImages.length
    },
  },
  watch: {
    files (newValue) {
      this.previewImages = newValue
    },
  },
  methods: {
    showFileDialog () {
      this.$refs.input_file.click()
    },
    removeFile (index) {
      let remove_image = this.previewImages[index]
      // 即時削除
      if (this.immediateDelete) {
        this.$emit('removeFile', remove_image, index)
      } else {
        // 既存ファイル
        if (remove_image.id) {
          remove_image._destroy = true
          this.$set(this.previewImages, index, remove_image)
        } else {
          this.previewImages.splice(index, 1)
        }
        // this.emitUpdateImages()
      }
    },
    isEmpty (val) {
      return (val === null || val === '')
    },
    isImageContent (type) {
      return type.startsWith('image')
    },
    isPdfContent (type) {
      return type.endsWith('pdf')
    },
    documentIcon(type) {
      return (this.isPdfContent(type)) ? "mdi-file-pdf-box" : "mdi-file-document-outline"
    },
    convertSize (size) {
      if (this.isEmpty(size)) return size
      const kb = 1024
      const mb = Math.pow(kb, 2)

      let target = null
      let unit = 'byte'

      if (size >= mb) {
        target = mb
        unit = 'MB'
      } else if (size >= kb) {
        target = kb
        unit = 'KB'
      }
      const res = target !== null ? Math.floor((size / target) * 100) / 100 : size
      return `${res} ${unit}`
    },
    onDrop (event) {
      this.dragging = false
      this.addFiles(event.dataTransfer.files)
    },
    fileSelected (event) {
      this.addFiles(event.target.files)
    },
    addFiles (files) {
      if (this.isOverMaxFiles(files)) {
        // 最大枚数を超える場合は処理しない
        alert(this.maxFilesAlertMessage)
      } else if (this.isInValidFileType(files)) {
        alert(this.acceptAlertMessage)
      } else if (this.isOverMaxFileSize(files)) {
        alert(this.maxFileSizeAlertMessage)
      } else {
        this.generatePreviewImages(files)
      }
    },
    generatePreviewImages (files) {
      for (const file of files) {
        this.toDataURI(file).then((image) => {
          const imgObj = {
            url: image,
            file: file,
            content_type: file.type,
            name: file.name,
            size: file.size,
            _destroy: false,
          }
          this.previewImages.push(imgObj)
          this.emitUpdateFiles()
        })
      }
    },
    emitUpdateFiles () {
      this.refreshKey++
      this.$emit("update:files", this.previewImages)
      this.$emit('fileChanged', this.isFileChanged)
    },
    isOverMaxFileSize(files) {
      if (this.maxFileSize <= 0) return false

      let isInValid = false
      for (const file of files) {
        if (file.size > this.maxFileSize) {
          isInValid = true
          break
        }
      }
      // true: 上限を超えるファイルが含まれている
      return isInValid
    },
    isOverMaxFiles (files) {
      const length = this.previewableImages.length + files.length
      return length > this.maxFiles
    },
    isInValidFileType (files) {
      let isInValid = false
      for (const file of files) {
        const isSome = this.accepts.some(
          a => a === file.type
        )

        if (!isSome) {
          isInValid = true
          break
        }
      }
      // true: 許可しないものが含まれている
      return isInValid
    },
    // File オブジェクトを DataURI に変換する
    toDataURI (file) {
      return new Promise(resolve => {
        const fileReader = new FileReader()

        fileReader.onload = () => {
          resolve(fileReader.result)
        }
        fileReader.readAsDataURL(file)
      })
    }
  }
}

</script>

<style scoped lang="scss">
</style>
